import { useCallback, useMemo, useState } from "react"
import AnesthetistSignatureForm from "./AnesthetistSignatureForm"
import { useAnswersStore, useCompanyStore, usePatientStore } from "../../stores"
import { showErrorToast } from "@amigoapp/doca-react"
import { useHistory } from 'react-router-dom';
import { answerQuestionary } from "../../processes/anesthetist-questionary";
import { clearMask } from "../../utils/formatter";

const AnesthetistSignatureFormContainer = () => {
	const { innerWidth: width } = window
	const { companySettings: { settings } } = useCompanyStore();
	const { patient } = usePatientStore()
	const history = useHistory()
	const { answers } = useAnswersStore()

	const canvasOptions = useMemo(
		() => ({
			height: 150,
			width: width > 600 ? 600 : width - 32,
			className: 'doca-w-full doca-h-full'
		}),
		[width]
	)

	const [loading, setLoading] = useState()
	const [signatureCanvas, setSignatureCanvas] = useState()
	const [isResponsibleAdded, setIsResponsibleAdded] = useState(false)
	const [responsible, setResponsible] = useState({
		name: patient?.responsible_name || "",
		cpf: patient?.cpf_responsible || ""
	})

	const getCanvasRef = useCallback((ref) => setSignatureCanvas(ref), [])


	const handleCheckboxChange = useCallback(() => {
		setIsResponsibleAdded((prevState) => !prevState)
	}, [])

	const getSignatureAsBase64 = useCallback(() => {
		if (signatureCanvas && !signatureCanvas.isEmpty()) {
			return signatureCanvas.toDataURL("image/png")
		}
		return null
	}, [signatureCanvas])

	const validateForm = useCallback(() => {
		if (!signatureCanvas || signatureCanvas.isEmpty()) {
			showErrorToast("Por favor, adicione uma assinatura")
			return false
		}

		if (isResponsibleAdded && (!responsible.name || !responsible.cpf)) {
			showErrorToast("Por favor, preencha os dados do responsável")
			return false
		}

		return true
	}, [signatureCanvas, isResponsibleAdded, responsible])

	const mountQuestionaryData = useCallback((signatureBase64) => {
		const questionaryData = answers?.questionary || [];
		const patientChanges = {};

		if (answers?.patient_changes?.height) {
			patientChanges.height = answers.patient_changes.height;
		}

		if (answers?.patient_changes?.weight) {
			patientChanges.weight = answers.patient_changes.weight;
		}

		if (isResponsibleAdded) {
			if (responsible.name) {
				patientChanges.responsible_name = responsible.name;
			}

			if (responsible.cpf) {
				patientChanges.responsible_cpf = clearMask(responsible.cpf);
			}
		}

		return {
			signature_base64: signatureBase64,
			questionary: questionaryData,
			patient_changes: patientChanges
		};
	}, [answers, isResponsibleAdded, responsible]);

	const handleSubmit = useCallback(async () => {
		if (!validateForm()) return

		setLoading(true)

		const signatureBase64 = getSignatureAsBase64()

		if (!signatureBase64) {
			showErrorToast("Erro ao processar a assinatura. Por favor, tente novamente.")
			setLoading(false)
			return
		}

		const bodyData = mountQuestionaryData(signatureBase64)

		await answerQuestionary(bodyData)

		setLoading(false)

		history.push(`/scheduled-succesfully?token=${localStorage.getItem('token')}`);
	}, [validateForm, getSignatureAsBase64, history, mountQuestionaryData]);


	const undo = useCallback(() => {
		if (signatureCanvas && !signatureCanvas.isEmpty()) {
			const newCanvas = signatureCanvas.toData()
			newCanvas.pop()
			signatureCanvas.fromData(newCanvas)
		}
	}, [signatureCanvas])

	const clear = useCallback(() => {
		return signatureCanvas?.clear()
	}, [signatureCanvas])

	return (
		<AnesthetistSignatureForm
			loading={loading}
			variant={settings?.theme?.name}
			getCanvasRef={getCanvasRef}
			canvasOptions={canvasOptions}
			isResponsibleAdded={isResponsibleAdded}
			handleCheckboxChange={handleCheckboxChange}
			handleSubmit={handleSubmit}
			clear={clear}
			undo={undo}
			responsible={responsible}
			setResponsible={setResponsible}
		/>
	)
}

export default AnesthetistSignatureFormContainer
